import React, { Component } from 'react';
import TagManager from 'react-gtm-module';
import { toDecimals } from '@h/currency';
import withCurrencyConfig from '@c/WithCurrencyConfig';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as hookActions from 'checkout/actions/hooks';
import { getMainEvent, getSelectedEvent, isSeries } from '@b/checkout/selectors/event';
import { getSelectedDeliveryOption } from '@b/checkout/selectors/delivery';
import { getOrderTotal } from '@b/checkout/selectors/cart';
import isEqual from 'lodash/isEqual';

class EcommerceManager extends Component {
  constructor(props) {
    super(props);

    this.state = {
      previousAdditionalFees: [],
      previousProductsImpressions: [],
      previousBundlesImpressions: [],
      previousPriceLevelsImpressions: [],
    }

    this.props.addTicketsAddedToCartHook(this.onTicketsAdded);
    this.props.addProductsAddedToCartHook(this.onProductsAdded);
    this.props.addBundlesAddedToCartHook(this.onBundlesAdded);
    this.props.addReservationDataSetHook(this.onReservationDataSet);
    this.props.addReservationCompleteHook(this.onReservationComplete);
    this.props.addStateTransitionHook(this.onStateTransition);
    this.props.addGoToCheckoutHook(this.onGoToCheckout);
    this.props.addSetPriceLevelsHook(this.onPriceLevelsSet);
    this.props.addSetBundlesHook(this.onBundlesSet);
    this.props.addSetProductsHook(this.onProductsSet);
  }

  componentDidMount() {
    if (this.props.currentRouteName === 'ticketPicker' && this.props.overlayVisible) {
      const dataLayer = this.getBaseDataLayer();
      dataLayer.ecommerce.impressions = this.getAllImpressions({
        priceLevels: this.props.priceLevels,
        bundles: this.props.bundles,
        products: this.props.products,
      });

      this.report({ dataLayer });
    }
  }

  report = (data) => {
    TagManager.dataLayer(data);
  }

  getBaseDataLayer = () => ({
    'ecommerce': {
      'currencyCode': this.getCurrencyCode(),
    }
  })

  getAllImpressions = ({
    priceLevels,
    bundles,
    products,
  }) => {
    const priceLevelImpressions = this.getItemsImpressions(priceLevels, 'level');
    const bundleImpressions = this.getItemsImpressions(bundles, 'bundle');
    const productImpressions = this.getItemsImpressions(products, 'product');

    return [
      ...priceLevelImpressions,
      ...bundleImpressions,
      ...productImpressions,
    ];
  }

  getIdPropertyName = (type) => {
    switch (type) {
      case 'level':
        return 'price_level_id';
      case 'bundle':
        return 'id';
      case 'product':
        return 'product_id';
      default:
        return 'id';
    }
  }

  getId = (item, type) => {
    const { mainEvent, selectedEvent, isSeries } = this.props;

    const getSeriesValues = () => {
      // bundles have really weird structure, so we need to handle it separately
      if (type === 'bundle' && !item.parent_id) {
        const bundle = this.props.bundles[item.bundle_id];
        const bundle_parent_id = bundle?.parent_id;

        return [
          mainEvent?.event_id,
          item.eventId || item.event_id || selectedEvent?.event_id, 
          type,
          bundle_parent_id,
          item.bundle_id || item.id,
        ];
      }

      return [
        mainEvent?.event_id,
        item.eventId || item.event_id || selectedEvent?.event_id, 
        type,
        item.parent_id,
        item[this.getIdPropertyName(type)],
      ];
    };

    const getSingleValues = () => {
      return [
        item.eventId || item.event_id || selectedEvent?.event_id,
        type,
        item[this.getIdPropertyName(type)],
      ];
    };

    return (isSeries ? getSeriesValues() : getSingleValues())
      .filter(Boolean)
      .join('-');
  }

  getItemsImpressions = (items, type) => {
    return Object.values(items).map((item, index) => {
      return {
        'name': item.name,
        'id': this.getId(item, type),
        'price': toDecimals({ amount: item.price.price || item.price.amount, currency: item.price.currency }),
        'brand': this.props.contactInfo.name,
        'category': this.props.eventName,
        'position': item.position || (index + 1),
        'metric4': toDecimals({ amount: item.service_fee.price, currency: item.service_fee.currency }),
      };
    });
  }

  getCurrencyCode = () => {
    if (this.props.reservationCurrency) {
      return this.props.reservationCurrency;
    }

    if (Object.values(this.props.priceLevels).length > 0) {
      return this.props.priceLevels[Object.keys(this.props.priceLevels)[0]].price.currency;
    }
    return 'USD';
  }

  addTicketsToProducts = (currentLayer, tickets, action) => {
    if (!currentLayer[action]) {
      currentLayer[action] = {};
    }

    const ticketsGroupedByPriceLevelId = tickets.reduce((acc, ticket) => {
      const key = `${ticket.event_id}-level-${ticket.price_level_id}`;
      if (!acc[key]) {
        acc[key] = ticket;
      }
      acc[key].quantity = (acc[key].quantity || 0) + 1;
      return acc;
    }, {});

    currentLayer[action].products = [
      ...currentLayer[action]?.products || [],
      ...Object.values(ticketsGroupedByPriceLevelId).map((ticket) => ({
        name: ticket.price_level_name,
        id: this.getId(ticket, 'level'),
        price: toDecimals({ amount: ticket.cost.amount, currency: ticket.cost.currency }),
        category: ticket.event_name,
        brand: this.props.contactInfo.name,
        quantity: ticket.quantity,
        metric4: toDecimals({ amount: ticket.buyer_fee.amount, currency: ticket.buyer_fee.currency }),
      })),
    ];
  }

  addProductsToProducts = (currentLayer, products, action) => {
    if (!currentLayer[action]) {
      currentLayer[action] = {};
    }

    const productsGroupedByProductId = products.reduce((acc, product) => {
      const key = `${product.product_id}-config-${product.configuration_id}`;
      if (!acc[key]) {
        acc[key] = product;
      }
      acc[key].quantity = (acc[key].quantity || 0) + 1;
      return acc;
    }, {});

    currentLayer[action].products = [
      ...currentLayer[action]?.products || [],
      ...Object.values(productsGroupedByProductId).map((product) => ({
          name: product.product_name,
          id: this.getId(product, 'product'),
          price: toDecimals({ amount: product.cost.amount, currency: product.cost.currency }),
          category: product.event_name,
          brand: this.props.contactInfo.name,
          variant: product.display_name,
          quantity: product.quantity,
          metric4: toDecimals({ amount: product.buyer_fee.amount, currency: product.buyer_fee.currency }),
      })),
    ];
  }

  addBundlesToProducts = (currentLayer, bundles, action) => {
    if (!currentLayer[action]) {
      currentLayer[action] = {};
    }

    const bundleGroupedByBundleId = bundles.reduce((acc, bundle) => {
      const key = `${bundle.event_id}-bundle-${bundle.bundle_id}`;
      if (!acc[key]) {
        acc[key] = bundle;
      }
      acc[key].quantity = (acc[key].quantity || 0) + 1;
      return acc;
    }, {});

    currentLayer[action].products = [
      ...currentLayer[action]?.products || [],
      ...Object.values(bundleGroupedByBundleId).map((bundle) => ({
        name: bundle.name,
        id: this.getId(bundle, 'bundle'),
        price: toDecimals({ amount: bundle.cost.amount, currency: bundle.cost.currency }),
        category: bundle.event_name,
        brand: this.props.contactInfo.name,
        variant: bundle.display_name,
        quantity: bundle.quantity || 1,
        metric4: toDecimals({ amount: bundle.buyer_fee.amount, currency: bundle.buyer_fee.currency }),
      })),
    ];
  }

  addFeesToProducts = (currentLayer, fees, action) => {
    if (!currentLayer[action]) {
      currentLayer[action] = {};
    }
    currentLayer[action].products = [
      ...currentLayer[action]?.products || [],
      ...fees.map((fee) => ({
        name: fee.name,
        id: fee.name,
        price: toDecimals({ amount: fee.fee_details.amount, currency: fee.currency }),
        category: 'Fee',
        quantity: 1,
      })),
    ];
  }

  onTicketsAdded = (tickets) => {
    const dataLayer = this.getBaseDataLayer();

    this.addTicketsToProducts(dataLayer.ecommerce, tickets, 'add');

    this.report({ dataLayer});
  }

  onProductsAdded = (products) => {
    const dataLayer = this.getBaseDataLayer();

    this.addProductsToProducts(dataLayer.ecommerce, products, 'add');

    this.report({ dataLayer });
  }

  onBundlesAdded = (bundles) => {
    const dataLayer = this.getBaseDataLayer();

    this.addBundlesToProducts(dataLayer.ecommerce, bundles, 'add');

    this.report({ dataLayer });
  }

  onReservationDataSet = (reservation) => {
    if (JSON.stringify(reservation?.attributes?.additional_fees) === JSON.stringify(this.state.previousAdditionalFees)) {
      return;
    }

    this.setState({ previousAdditionalFees: reservation?.attributes?.additional_fees || [] });

    const dataLayer = this.getBaseDataLayer();

    this.addFeesToProducts(dataLayer.ecommerce, reservation?.attributes?.additional_fees, 'add');

    this.report({ dataLayer });
  }

  onReservationComplete = () => {
    const dataLayer = this.getBaseDataLayer();
    dataLayer.ecommerce.purchase = {
      'actionField': {
        'id': this.props.reservation?.confirmation_number,
        'affiliation': this.props.contactInfo.name,
        'revenue': toDecimals({ amount: this.props.orderTotal, currency: this.props.reservationCurrency }),
        'tax': toDecimals({ amount: this.props.reservation?.total_fees?.amount || 0, currency: this.props.reservationCurrency }),
        'shipping': toDecimals({ amount: this.props.reservation?.total_delivery_fees?.amount || 0, currency: this.props.reservationCurrency }),
        'coupon': this.props.codes.join(', ') || '',
      },
      'products': [],
    };
    dataLayer.event = 'purchase';
    this.addTicketsToProducts(dataLayer.ecommerce, this.props.tickets, 'purchase');
    this.addProductsToProducts(dataLayer.ecommerce, this.props.productsSales, 'purchase');
    this.addBundlesToProducts(dataLayer.ecommerce, this.props.bundlesSales, 'purchase');
    this.addFeesToProducts(dataLayer.ecommerce, this.props.reservation?.additional_fees || [], 'purchase');

    this.report({ dataLayer });
  }

  onStateTransition = (state) => {
    if (state === 'checkout-2') {
      const secondStepDataLayer = this.getBaseDataLayer();
      secondStepDataLayer.ecommerce.checkout = {
        'actionField': {
          'step': 2,
          'option': this.props.deliveryOptionName,
        },
      };
      secondStepDataLayer.event = 'checkoutOption';

      TagManager.dataLayer({
        dataLayer: secondStepDataLayer,
      });

      const thirdStepDataLayer = this.getBaseDataLayer();
      thirdStepDataLayer.ecommerce.checkout = {
        'actionField': {
          'step': 3,
          'option': 'Credit Card'
        },
      };
      thirdStepDataLayer.event = 'checkoutOption';

      this.report({ dataLayer: thirdStepDataLayer });
    }

    if (state === 'ticketPicker') {
      const dataLayer = this.getBaseDataLayer();
      dataLayer.ecommerce.impressions = this.getAllImpressions({
        priceLevels: this.props.priceLevels,
        bundles: this.props.bundles,
        products: this.props.products,
      });

      this.report({ dataLayer });
    }
  }

  onGoToCheckout = () => {
    const reservationSessionKey = `EcommerceManager_${this.props.reservation?.confirmation_number}`;

    if (sessionStorage.getItem(reservationSessionKey)) {
      return;
    }

    const dataLayer = this.getBaseDataLayer();

    this.addTicketsToProducts(dataLayer.ecommerce, this.props.tickets, 'checkout');
    this.addProductsToProducts(dataLayer.ecommerce, this.props.productsSales, 'checkout');
    this.addBundlesToProducts(dataLayer.ecommerce, this.props.bundlesSales, 'checkout');
    this.addFeesToProducts(dataLayer.ecommerce, this.props.reservation?.additional_fees || [], 'checkout');

    dataLayer.ecommerce.checkout.actionField = {
      'step': 1,
    };

    dataLayer.event = 'checkout';

    this.report({ dataLayer });

    sessionStorage.setItem(reservationSessionKey, '1');
  }

  onPriceLevelsSet = (priceLevels, empty) => {
    if (empty || !this.props.overlayVisible) {
      return;
    }
    
    const impressions = this.getItemsImpressions(priceLevels, 'level');

    if (isEqual(impressions, this.state.previousPriceLevelsImpressions)) {
      return;
    }

    this.setState({ previousPriceLevelsImpressions: impressions });
    
    const dataLayer = this.getBaseDataLayer();
    dataLayer.ecommerce.impressions = impressions;

    this.report({ dataLayer });
  }

  onBundlesSet = (bundles, empty) => {
    if (empty || !this.props.overlayVisible) {
      return;
    }

    const impressions = this.getItemsImpressions(bundles, 'bundle');

    if (isEqual(impressions, this.state.previousBundlesImpressions)) {
      return;
    }

    this.setState({ previousBundlesImpressions: impressions });

    const dataLayer = this.getBaseDataLayer();
    dataLayer.ecommerce.impressions = impressions;

    this.report({ dataLayer });
  }

  onProductsSet = (products, empty) => {
    if (empty || !this.props.overlayVisible) {
      return;
    }

    const impressions = this.getItemsImpressions(products, 'product');

    if (isEqual(impressions, this.state.previousProductsImpressions)) {
      return;
    }

    this.setState({ previousProductsImpressions: impressions });

    const dataLayer = this.getBaseDataLayer();
    dataLayer.ecommerce.impressions = impressions;

    this.report({ dataLayer });
  }

  render() {
    return null;
  }
}

const mapStateToProps = (state) => {
  const mainEvent = getMainEvent(state)?.event;
  const selectedEvent = getSelectedEvent(state)?.event;

  return {
    mainEvent: mainEvent ?? null,
    selectedEvent: selectedEvent ?? null,
    isSeries: isSeries(state),
    eventName: mainEvent?.name || '',
    priceLevels: state.priceLevel.levels,
    bundles: state.bundles.bundles,
    products: state.products.products,
    contactInfo: state.contactInfo,
    tickets: state.tickets.tickets,
    productsSales: state.productSales.products,
    bundlesSales: state.bundleSales.bundles,
    reservation: state.reservation,
    reservationCurrency: state.reservation.currency,
    deliveryOptionName: getSelectedDeliveryOption(state)?.name || '',
    orderTotal: getOrderTotal(state)?.amount ?? 0,
    codes: state.coupon.codes || [],
    currentRouteName: state.ui.currentRouteName,
    overlayVisible: state.ui.overlay,
  };
};

const mapDispatchToProps = dispatch => {
    const actions = {};
    Object.keys(hookActions).forEach(hookActionName => {
        actions[hookActionName] = hookActions[hookActionName];
    });
    return bindActionCreators(actions, dispatch);
}

export default withCurrencyConfig(connect(mapStateToProps, mapDispatchToProps)(EcommerceManager));
